import Button from 'components/Button';
import { Form, RadioField, SelectField, TimeField } from 'components/Form';
import { SmartContainer, SmartInline, SmartItem } from 'components/SmartContainer/SmartContainer';
import { useCallback, useEffect, useState } from 'react';
import { GenericFilterOperationEnum } from 'services/tenantManagementService';
import { additionalFilterOptions, AndOrEnum, findAllFilters } from '../../filtersHelper';
import { FilterType, TimeColumnModel } from 'components/Table';
import { TimeFilterModel } from './TimeFilterModel';

type Props = {
	column: TimeColumnModel
	filters: FilterType[]
	onSave: (filters: FilterType[]) => void
	onCancel: () => void
}

const filterTypeOptions = [
	{ value: GenericFilterOperationEnum.EQUALS, text: 'i18n.label.filter.operation.equals' },
	{ value: GenericFilterOperationEnum.NOT_EQUALS, text: 'i18n.label.filter.operation.notEquals' },
	{ value: GenericFilterOperationEnum.GREATER_EQUAL_THAN, text: 'i18n.label.filter.operation.greaterEqualThan' },
	{ value: GenericFilterOperationEnum.GREATER_THAN, text: 'i18n.label.filter.operation.greaterThan' },
	{ value: GenericFilterOperationEnum.LESS_EQUAL_THAN, text: 'i18n.label.filter.operation.lessEqualThen' },
	{ value: GenericFilterOperationEnum.LESS_THAN, text: 'i18n.label.filter.operation.lessThan' },
];

export const TimeFilter = ({ column, filters, onSave, onCancel }: Props) => {
	const [values, setValues] = useState(new TimeFilterModel());

	useEffect(
		() => {
			const newValues = new TimeFilterModel();

			const currentFilters = findAllFilters(filters, column.id);
			// if there are two filters that means AND filter is used
			if (currentFilters && currentFilters.length === 2) {
				newValues.additionalFilter = AndOrEnum.And;
				newValues.filterType1 = currentFilters[0].operation;
				newValues.filterValue1 = currentFilters[0].value;
				newValues.filterType2 = currentFilters[1].operation;
				newValues.filterValue2 = currentFilters[1].value;
				// if there is one filter, but without OR filters
			} else if (currentFilters && currentFilters.length === 1 && (!currentFilters[0].orFilters || currentFilters[0].orFilters.length === 0)) {
				newValues.additionalFilter = AndOrEnum.And;
				newValues.filterType1 = currentFilters[0].operation;
				newValues.filterValue1 = currentFilters[0].value;
				// if there is one filter, with two OR filters
			} else if (currentFilters && currentFilters.length === 1 && currentFilters[0].orFilters && currentFilters[0].orFilters.length === 2) {
				newValues.additionalFilter = AndOrEnum.Or;
				newValues.filterType1 = currentFilters[0].orFilters[0].operation;
				newValues.filterValue1 = currentFilters[0].orFilters[0].value;
				newValues.filterType2 = currentFilters[0].orFilters[1].operation;
				newValues.filterValue2 = currentFilters[0].orFilters[1].value;
				// if there is one filter, with one OR filters
			} else if (currentFilters && currentFilters.length === 1 && currentFilters[0].orFilters && currentFilters[0].orFilters.length === 1) {
				newValues.additionalFilter = AndOrEnum.Or;
				newValues.filterType1 = currentFilters[0].orFilters[0].operation;
				newValues.filterValue1 = currentFilters[0].orFilters[0].value;
			}

			setValues(newValues);
		},
		[filters, column.id]
	)

	const submitCallback = useCallback(
		async () => {
			let newFilters = [...filters];
			// remove current filters with specific columnId
			const currentFilters = findAllFilters(newFilters, column.id);
			if (currentFilters.length > 0) {
				currentFilters.forEach(filter => {
					const index = newFilters.indexOf(filter);
					newFilters.splice(index, 1);
				})
			}

			const newFilter: FilterType = {};
			// If OR filter is selected, add filters to orFilters array
			if (values.additionalFilter === AndOrEnum.Or) {
				newFilter.orFilters = [];
				if (values.filterType1) {
					newFilter.orFilters.push({
						columnId: column.id,
						operation: values.filterType1,
						value: values.filterValue1
					});
				}

				if (values.filterType2) {
					newFilter.orFilters.push({
						columnId: column.id,
						operation: values.filterType2,
						value: values.filterValue2
					});
				}

				if (newFilter.orFilters.length > 0) {
					newFilters.push(newFilter);
				}
			}
			// If AND filter is selected add "Parent" filter
			if (values.additionalFilter === AndOrEnum.And) {
				if (values.filterType1) {
					newFilters.push({
						columnId: column.id,
						operation: values.filterType1,
						value: values.filterValue1
					});
				}

				// If second filter is selected add new "Parent" filter
				if (values.filterType2) {
					newFilters.push({
						columnId: column.id,
						operation: values.filterType2,
						value: values.filterValue2
					});
				}
			}

			onSave && await onSave(newFilters);
		},
		[filters, column.id, onSave, values]
	)

	const clearCallback = useCallback(
		() => {
			setValues(new TimeFilterModel());
		},
		[]
	)

	return (
		<Form
			values={values}
			onChange={setValues}
			onSubmit={submitCallback}
			onCancel={onCancel}
			submitButtonText='i18n.button.apply'
			disableUnsavedChangesGuard
			render={() => (
				<SmartContainer>
					<SmartItem>
						<SmartInline>
							<SelectField
								id='filterType1'
								items={filterTypeOptions}
								getItemId={(item) => item.value}
								getItemText={(item) => item.text}
								inline
							/>
							<TimeField
								id='filterValue1'
								inline
								isDuration={column.isDuration}
							/>
						</SmartInline>
						<RadioField
							id='additionalFilter'
							items={additionalFilterOptions}
						/>
						<SmartInline>
							<SelectField
								id='filterType2'
								items={filterTypeOptions}
								getItemId={(item) => item.value}
								getItemText={(item) => item.text}
								inline
							/>
							<TimeField
								id='filterValue2'
								inline
								isDuration={column.isDuration}
							/>
						</SmartInline>
					</SmartItem>
				</SmartContainer>
			)}
			renderAdditionalButtons={() => (
				<Button
					text='i18n.button.clear'
					color='destructive'
					onClick={clearCallback}
				/>
			)}
		/>
	)
}
